<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import PipelineHeader from './PipelineHeader.vue';
import { usePipelinePermissions } from '../composables/usePipelinePermissions';
import Modal from 'dashboard/components/Modal.vue';
import PipelineCardForm from './PipelineCardForm.vue';
import PipelineFilter from './PipelineFilter.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import { getStageColorClasses } from './pipelineTemplates';
import { useAlert } from 'dashboard/composables';
import {
  getCardCurrency,
  getCardPriority,
  getCardValue,
  matchesCardSearch,
  matchesColumnFilters,
  resolveCardAgent,
} from '../helpers/columnFilterMatchers';
import {
  escapeCsvCell,
  formatInReportingTimezone,
  formatLocalizedDateInTimezone,
  formatLocalizedCurrency,
  resolveAccountCurrency,
  resolveReportingTimezone,
  sanitizeDownloadFilename,
} from '../utils/localeFormatting';
import { PIPELINE_PAGINATION_ERROR_CODE } from '../helpers/pipelinePagination';

const props = defineProps({
  currentView: {
    type: String,
    default: 'list',
  },
  pipelineCards: {
    type: Array,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  // Props vindas de PipelineView.vue:411-419 (antes órfãs por
  // fragment-root; ver bug report 2026-04-22 #3). Declaradas para
  // absorver os attrs do pai mesmo quando ListTab não consome (a
  // alternativa seria deixá-los virar warning + atributos HTML
  // soltos no DOM root). O linter reclama de "never used" mas o
  // uso é semântico: bloquear inheritance no DOM.
  // eslint-disable-next-line vue/no-unused-properties
  labelsMap: {
    type: Object,
    default: () => ({}),
  },
  // eslint-disable-next-line vue/no-unused-properties
  itemId: {
    type: [String, Number],
    default: null,
  },
  // eslint-disable-next-line vue/no-unused-properties
  forceReload: {
    type: Number,
    default: 0,
  },
});
// Bug 3 (2026-04-22): emits 'items-updated', 'item-updated' e 'back'
// declarados em PipelineView.vue:421-424 mas não estavam aqui — Vue 3
// não conseguia rotear porque template tinha fragment-root e atributos
// extras viraram warning + ignored. handleEdit setava state mas a
// Modal não estava no template, então usuário clicava lápis e nada
// acontecia visualmente. Agora ambos resolvidos: emits declarados +
// Modal de edit renderizada (ver template). Kebab-case mantido pra
// alinhar com o que PipelineView envia (`@item-updated`, `@items-updated`).
/* eslint-disable vue/no-unused-emit-declarations, vue/custom-event-name-casing */
const emit = defineEmits([
  'switch-view',
  'itemClick',
  'forceUpdate',
  'items-updated',
  'item-updated',
  'back',
]);
/* eslint-enable vue/no-unused-emit-declarations, vue/custom-event-name-casing */
const { t, locale } = useI18n();
const store = useStore();
const errorMessage = ref(null);
const searchQuery = ref('');
const activeFilters = computed(
  () => store.getters['pipelineCards/getActiveFilters'] || {}
);
const showFilterModal = ref(false);

const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);
const pipelineLoadError = computed(() => {
  const loadError = errorMessage.value || store.state.pipelineCards.error;
  if (loadError === PIPELINE_PAGINATION_ERROR_CODE) {
    return t('PIPELINE.DASHBOARD.PAGINATION_ERROR');
  }
  return (
    loadError ||
    (!selectedPipeline.value?.id
      ? t('PIPELINE.ERRORS.NO_PIPELINE_SELECTED')
      : null)
  );
});
const { canViewPipeline, canDeleteCard } =
  usePipelinePermissions(selectedPipeline);
const reportingTimezone = computed(() => resolveReportingTimezone(store));
const accountCurrency = computed(() => resolveAccountCurrency(store));
const availableAgents = computed(
  () =>
    store.getters['agents/getVerifiedAgents'] ||
    store.getters['agents/getAgents'] ||
    []
);

const resolveAgent = item => {
  return resolveCardAgent(item, availableAgents.value);
};

// Derive rows directly from reactive cards/agents instead of copying them into
// a ref behind a shallow watcher. Action Cable mutates existing card records in
// place, so the list must track the fields it renders rather than depend on a
// new array identity arriving from the store.
const deals = computed(() => {
  const currentPipeline = selectedPipeline.value;
  if (!currentPipeline?.id) return [];

  return props.pipelineCards
    .filter(item => String(item.pipeline_id) === String(currentPipeline.id))
    .map(item => {
      const stageName = currentPipeline.stages[item.pipeline_stage]?.name || '';
      const stageColor =
        currentPipeline.stages[item.pipeline_stage]?.color || '#64748B';

      return {
        id: item.id,
        title: item.title || item.item_details?.title || '',
        description: item.description || item.item_details?.description || '',
        priority: getCardPriority(item),
        assignee: resolveAgent(item),
        value: getCardValue(item),
        currency: getCardCurrency(item, accountCurrency.value),
        // Auditoria 2026-08-08 (F03): o serializer do board entrega o score na
        // RAIZ; a projeção de item_details não o inclui, então ler só de lá
        // deixava o badge sempre vazio na Lista.
        leadScore: item.lead_score ?? item.item_details?.lead_score ?? null,
        contactName: item.item_details?.contact_name || '',
        contactThumbnail: item.item_details?.contact_thumbnail || '',
        stage: item.pipeline_stage,
        stageName,
        stageColor,
        createdAt: item.created_at,
        next_activity_at:
          item.next_activity_at ||
          item.item_details?.next_activity_at ||
          item.item_details?.scheduled_at ||
          item.item_details?.deadline_at,
        last_pipeline_activity_at:
          item.last_pipeline_activity_at ||
          item.item_details?.last_pipeline_activity_at ||
          item.updated_at,
        conversation_last_activity_at:
          item.conversation_last_activity_at ||
          item.item_details?.conversation?.last_activity_at,
        item_details: item.item_details,
        custom_attributes: item.custom_attributes || {},
        pipeline_id: String(item.pipeline_id),
        pipeline_stage: item.pipeline_stage,
        raw: item,
      };
    });
});

// Ciclo 7 — teto silencioso de 500 cards na lista. O fetch global traz no
// máximo 500/página; quando meta.has_more, o backend manda meta.next_cursor
// (keyset global (position, id)) e a lista oferece "Carregar mais" no rodapé.
// meta.stage_counts traz o TOTAL real por stage (server-side) — usado nos
// badges dos grupos no lugar de contar o flat parcial ("Lead 177" enganava).
const cardsMeta = computed(() => store.getters['pipelineCards/getMeta'] || {});
const isAppendingFromServer = computed(
  () => store.state.pipelineCards.appending
);
const hasMoreFromServer = computed(
  () =>
    // Mode-aware: has_more/next_cursor podem ser resquício de um load global
    // anterior quando o último load foi do board (que só atualiza
    // stage_counts) — sem o check, o rodapé apareceria e o clique seria
    // no-op (o store bloqueia append fora do modo global; achado Codex).
    store.state.pipelineCards.lastFetchedMode === 'global' &&
    Boolean(cardsMeta.value.has_more) &&
    Boolean(cardsMeta.value.next_cursor)
);
const hasPartialDataset = computed(
  () =>
    store.state.pipelineCards.lastFetchedMode === 'global' &&
    Boolean(cardsMeta.value.has_more)
);
const serverStageCounts = computed(() => cardsMeta.value.stage_counts || {});
const serverTotalCount = computed(() =>
  Object.values(serverStageCounts.value).reduce(
    (sum, count) => sum + (Number(count) || 0),
    0
  )
);
// Busca/filtros da lista são client-side sobre o flat carregado; nesses casos
// o badge volta a contar o filtrado local (stage_counts não reflete a busca).
const hasActiveFilters = computed(() => {
  const filters = activeFilters.value;
  if (!filters) return false;
  return Boolean(
    filters.priority?.length ||
      (filters.value?.min !== null && filters.value?.min !== undefined) ||
      (filters.value?.max !== null && filters.value?.max !== undefined) ||
      filters.agent_id ||
      filters.date?.start ||
      filters.date?.end ||
      filters.status ||
      filters.stages?.length ||
      filters.sla_exceeded ||
      filters.labels?.length
  );
});
const hasClientSideFiltering = computed(
  () => Boolean(searchQuery.value.trim()) || hasActiveFilters.value
);
const stageServerTotal = stage => {
  if (hasClientSideFiltering.value) return null;
  const count = serverStageCounts.value[stage.id];
  return count === undefined ? null : Number(count) || 0;
};
const stageCountLabel = stage => {
  const total = stageServerTotal(stage);
  if (total === null || total <= stage.deals.length) {
    return String(total ?? stage.deals.length);
  }
  return t('PIPELINE.LIST.STAGE_COUNT_PARTIAL', {
    loaded: stage.deals.length,
    total,
  });
};

const handleLoadMoreFromServer = async () => {
  if (isAppendingFromServer.value) return;
  try {
    await store.dispatch('pipelineCards/fetchPipelineCardItems', {
      append: true,
    });
  } catch (error) {
    // Erro já toasted por handlePipelineError no store; o botão permanece
    // (has_more não muda em falha) e o usuário pode tentar de novo.
  }
};

const retryListLoad = async () => {
  errorMessage.value = null;
  try {
    await store.dispatch('pipelineCards/fetchPipelineCardItems');
  } catch (error) {
    const loadError = store.state.pipelineCards.error || error?.code;
    errorMessage.value =
      loadError === PIPELINE_PAGINATION_ERROR_CODE
        ? t('PIPELINE.DASHBOARD.PAGINATION_ERROR')
        : error?.message || t('PIPELINE.ERRORS.FETCH_ITEMS');
  }
};

const showDeleteModal = ref(false);
const itemToDelete = ref(null);
const isDeleting = ref(false);

// Adicione um ref para controlar as etapas expandidas
const expandedStages = ref(new Set()); // Começa com todas as etapas fechadas

// UX C1 (2026-04-22): Pagination simples por stage para evitar
// renderizar 1000+ DOM nodes de uma vez quando o pipeline tem
// muitos cards. Default: mostra primeiros 30 por stage; "Carregar
// mais" incrementa em +30. Solução pragmática (sem lib externa
// de virtualização) — resolve 90% dos casos com risco mínimo.
const STAGE_RENDER_INITIAL = 30;
const STAGE_RENDER_INCREMENT = 30;
const stageRenderLimits = ref({});

const getStageRenderLimit = stageId =>
  stageRenderLimits.value[stageId] || STAGE_RENDER_INITIAL;

const getVisibleDeals = stage =>
  stage.deals.slice(0, getStageRenderLimit(stage.id));

const hasMoreDeals = stage =>
  stage.deals.length > getStageRenderLimit(stage.id);

const remainingDealsCount = stage =>
  Math.max(0, stage.deals.length - getStageRenderLimit(stage.id));

const loadMoreDeals = stageId => {
  const current = getStageRenderLimit(stageId);
  stageRenderLimits.value = {
    ...stageRenderLimits.value,
    [stageId]: current + STAGE_RENDER_INCREMENT,
  };
};

// Adicione estas refs para controlar o modal de edição
const showEditModal = ref(false);
const itemToEdit = ref(null);
const editCardFormRef = ref(null);

// Sorting state (B5 - List View Enhancement)
const sortConfig = ref({
  field: 'created_at',
  direction: 'desc',
});

// Available sort fields
const sortFields = [
  { id: 'title', label: 'PIPELINE.SORT_OPTIONS.TITLE', icon: 'list' },
  {
    id: 'value',
    label: 'PIPELINE.SORT_OPTIONS.VALUE',
    icon: 'credit-card-person',
  },
  {
    id: 'priority',
    label: 'PIPELINE.SORT_OPTIONS.PRIORITY',
    icon: 'priority-high',
  },
  {
    id: 'created_at',
    label: 'PIPELINE.SORT_OPTIONS.CREATED_AT',
    icon: 'calendar',
  },
  {
    id: 'next_activity',
    label: 'PIPELINE.SORT_OPTIONS.NEXT_ACTIVITY',
    icon: 'calendar-clock',
  },
  {
    id: 'last_activity',
    label: 'PIPELINE.SORT_OPTIONS.LAST_ACTIVITY',
    icon: 'arrow-clockwise',
  },
  {
    id: 'conversation_activity',
    label: 'PIPELINE.SORT_OPTIONS.CONVERSATION_ACTIVITY',
    icon: 'chat',
  },
];
const sortOptions = computed(() =>
  sortFields.map(field => ({ value: field.id, label: t(field.label) }))
);

// Priority order for sorting
const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1, none: 0 };

const toggleSortDirection = () => {
  sortConfig.value.direction =
    sortConfig.value.direction === 'asc' ? 'desc' : 'asc';
};

const timestamp = (value, emptyValue = 0) => {
  if (!value) return emptyValue;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? emptyValue : parsed;
};

// Computed para filtrar os deals baseado na busca e filtros
const filteredDeals = computed(() => {
  let filtered = deals.value;

  filtered = filtered.filter(
    deal =>
      matchesCardSearch(deal.raw, searchQuery.value, deal.stageName) &&
      matchesColumnFilters(deal.raw, activeFilters.value, {
        reportingTimezone: reportingTimezone.value,
      })
  );

  // Apply sorting (B5 - List View Enhancement)
  const { field, direction } = sortConfig.value;
  const sortedFiltered = [...filtered].sort((a, b) => {
    let comparison = 0;

    switch (field) {
      case 'title':
        comparison = (a.title || '').localeCompare(b.title || '');
        break;
      case 'value':
        comparison =
          (a.value ?? Number.NEGATIVE_INFINITY) -
          (b.value ?? Number.NEGATIVE_INFINITY);
        break;
      case 'priority':
        comparison =
          (priorityOrder[a.priority] || 0) - (priorityOrder[b.priority] || 0);
        break;
      case 'next_activity':
        comparison =
          timestamp(a.next_activity_at, Number.MAX_SAFE_INTEGER) -
          timestamp(b.next_activity_at, Number.MAX_SAFE_INTEGER);
        break;
      case 'last_activity':
        comparison =
          timestamp(a.last_pipeline_activity_at) -
          timestamp(b.last_pipeline_activity_at);
        break;
      case 'conversation_activity':
        comparison =
          timestamp(a.conversation_last_activity_at) -
          timestamp(b.conversation_last_activity_at);
        break;
      case 'created_at':
      default:
        comparison = timestamp(a.createdAt) - timestamp(b.createdAt);
        break;
    }

    return direction === 'asc' ? comparison : -comparison;
  });

  return sortedFiltered;
});

// Export to CSV (B5 - List View Enhancement)
const exportToCSV = () => {
  const currentPipeline = selectedPipeline.value;
  if (!currentPipeline || !filteredDeals.value.length) return;

  const headers = [
    t('PIPELINE.FORM.TITLE.LABEL'),
    t('PIPELINE.STAGE.LABEL'),
    t('PIPELINE.VALUE.LABEL'),
    t('PIPELINE.FORM.VALUE.CURRENCY'),
    t('PIPELINE.FORM.PRIORITY.LABEL'),
    t('PIPELINE.AGENT.LABEL'),
    t('PIPELINE.FILTER.DATE.LABEL'),
  ];

  const rows = filteredDeals.value.map(deal => [
    deal.title || '',
    deal.stageName || '',
    deal.value ?? 0,
    deal.currency,
    t(`PIPELINE.PRIORITY_LABELS.${(deal.priority || 'none').toUpperCase()}`),
    deal.assignee?.name || t('PIPELINE.NO_AGENT'),
    formatLocalizedDateInTimezone(deal.createdAt, {
      locale: locale.value,
      timeZone: reportingTimezone.value,
      dateStyle: 'short',
      timeStyle: 'short',
    }),
  ]);

  const csvContent = [
    headers.map(escapeCsvCell).join(','),
    ...rows.map(row => row.map(escapeCsvCell).join(',')),
  ].join('\n');

  const blob = new Blob(['\ufeff' + csvContent], {
    type: 'text/csv;charset=utf-8;',
  });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  const exportDate = formatInReportingTimezone(
    new Date(),
    'yyyy-MM-dd',
    reportingTimezone.value
  );
  const fileName = `${sanitizeDownloadFilename(currentPipeline.name)}_${exportDate}.csv`;

  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const handleSearch = query => {
  searchQuery.value = query;
};

const handleFilter = () => {
  showFilterModal.value = true;
};

const applyFilters = async filters => {
  showFilterModal.value = false;
  errorMessage.value = null;
  try {
    await store.dispatch('pipelineCards/setGlobalFilters', filters || {});
  } catch (error) {
    errorMessage.value =
      store.state.pipelineCards.error ||
      error?.message ||
      t('PIPELINE.ERRORS.FETCH_ITEMS');
  }
};

const searchResults = computed(() => {
  if (!searchQuery.value && !hasActiveFilters.value) {
    return { total: 0, stages: {} };
  }

  const results = {
    total: filteredDeals.value.length,
    stages: {},
  };

  filteredDeals.value.forEach(deal => {
    if (!results.stages[deal.stageName]) {
      results.stages[deal.stageName] = 0;
    }
    results.stages[deal.stageName] += 1;
  });

  return results;
});

// Novo computed para agrupar os deals por estágio com logging
const groupedDeals = computed(() => {
  const currentPipeline = selectedPipeline.value;
  if (!currentPipeline?.stages) {
    return [];
  }

  // Cria um mapa de grupos inicialmente vazios para todas as etapas do funil atual
  const groups = {};

  Object.entries(currentPipeline.stages).forEach(([stageId, stageInfo]) => {
    groups[stageId] = {
      id: stageId,
      name: stageInfo.name || '',
      color: stageInfo.color || '#64748B',
      deals: [],
      totals: {},
      position: stageInfo.position || 0,
    };
  });

  // Distribui os deals filtrados pelos grupos
  filteredDeals.value.forEach(deal => {
    if (groups[deal.stage]) {
      groups[deal.stage].deals.push(deal);
      if (deal.value !== null) {
        groups[deal.stage].totals[deal.currency] =
          (groups[deal.stage].totals[deal.currency] || 0) + deal.value;
      }
    }
  });

  // Converte para array e ordena por posição
  const result = Object.values(groups).sort((a, b) => a.position - b.position);

  return result;
});

// Handlers
const handleEdit = deal => {
  itemToEdit.value = deal.raw;
  showEditModal.value = true;
};

const handleDelete = deal => {
  if (!canDeleteCard.value) return;
  itemToDelete.value = deal;
  showDeleteModal.value = true;
};

const confirmDelete = async () => {
  if (!canDeleteCard.value || !itemToDelete.value?.id || isDeleting.value) {
    return;
  }

  try {
    isDeleting.value = true;
    await store.dispatch(
      'pipelineCards/deletePipelineCardItem',
      itemToDelete.value.id
    );
    // eslint-disable-next-line vue/custom-event-name-casing
    emit('items-updated');
    showDeleteModal.value = false;
    itemToDelete.value = null;
  } catch {
    useAlert(t('PIPELINE.ERROR_DELETING_ITEM'));
  } finally {
    isDeleting.value = false;
  }
};

const handleItemClick = deal => {
  emit('itemClick', deal.raw);
};

// Bug 3 (2026-04-22): chamado quando PipelineCardForm emite 'saved'.
// Fecha a Modal, limpa state e propaga 'item-updated' pro pai
// (PipelineView.vue:424 → handleItemsUpdated). Também emite
// 'forceUpdate' por compatibilidade com flow antigo de delete.
const handleItemUpdated = updated => {
  showEditModal.value = false;
  itemToEdit.value = null;
  // eslint-disable-next-line vue/custom-event-name-casing
  emit('item-updated', updated);
  emit('forceUpdate');
};

const requestEditModalClose = () =>
  editCardFormRef.value?.requestClose?.() ?? true;

const closeDeleteModal = () => {
  if (isDeleting.value) return;
  showDeleteModal.value = false;
  itemToDelete.value = null;
};

const safeFormatCurrency = (value, currency) => {
  try {
    return formatLocalizedCurrency(value, {
      currency,
      locale: locale.value,
    });
  } catch {
    return `${currency} ${value}`;
  }
};

const formatDealValue = deal =>
  deal.value === null
    ? t('PIPELINE.LIST.EMPTY_CELL')
    : safeFormatCurrency(deal.value, deal.currency);

const formatDealDate = value =>
  formatLocalizedDateInTimezone(value, {
    locale: locale.value,
    timeZone: reportingTimezone.value,
    dateStyle: 'short',
  }) || t('PIPELINE.LIST.EMPTY_CELL');

const formatStageTotal = stage => {
  const totals = Object.entries(stage.totals || {});
  if (!totals.length) return t('PIPELINE.LIST.EMPTY_CELL');
  return totals
    .map(([currency, value]) => safeFormatCurrency(value, currency))
    .join(' · ');
};

// Função para alternar expansão da etapa
const toggleStage = stageId => {
  if (expandedStages.value.has(stageId)) {
    expandedStages.value.delete(stageId);
  } else {
    expandedStages.value.add(stageId);
  }
};

// Função para verificar se uma etapa está expandida
const isStageExpanded = stageId => expandedStages.value.has(stageId);

// Get priority bar token class (B5 - List View Enhancement)
const getPriorityBarClass = priority => {
  const classes = {
    urgent: 'bg-n-ruby-9',
    high: 'bg-n-orange-9',
    medium: 'bg-n-amber-9',
    low: 'bg-n-blue-9',
    none: 'bg-transparent',
  };
  return classes[priority] || classes.none;
};

// Get lead score badge class
const getLeadScoreBadgeClass = score => {
  if (score == null) return '';
  if (score >= 80) return 'bg-n-teal-3 text-n-teal-11';
  if (score >= 60) return 'bg-n-blue-3 text-n-blue-11';
  if (score >= 40) return 'bg-n-amber-3 text-n-amber-11';
  if (score >= 20) return 'bg-n-orange-3 text-n-orange-11';
  return 'bg-n-alpha-2 text-n-slate-11';
};

// Get priority badge class (B5 - List View Enhancement)
const getPriorityBadgeClass = priority => {
  const classes = {
    urgent: 'bg-n-ruby-3 text-n-ruby-11 border-n-ruby-9',
    high: 'bg-n-orange-3 text-n-orange-11 border-n-orange-9',
    medium: 'bg-n-amber-3 text-n-amber-11 border-n-amber-9',
    low: 'bg-n-blue-3 text-n-blue-11 border-n-blue-9',
    none: 'bg-n-alpha-2 text-n-slate-11 border-n-weak',
  };
  return classes[priority] || classes.none;
};
</script>

<template>
  <!-- Bug 3 (2026-04-22): single root <div> evita fragment-root warning
       que impedia atributos extras (labels-map, item-id, force-reload)
       e listeners (@items-updated, @item-updated, @back) de inheritar
       quando ListTab é renderizado via <component :is> em PipelineView. -->
  <div class="flex flex-col h-full w-full">
    <!-- Access denied: agent is not a member of this pipeline -->
    <div
      v-if="!canViewPipeline"
      class="flex flex-col h-full w-full bg-n-background items-center justify-center p-8"
    >
      <div class="text-center max-w-sm">
        <fluent-icon
          icon="lock-closed"
          size="40"
          class="mx-auto mb-4 text-n-slate-10"
        />
        <h3 class="text-base font-semibold text-n-slate-12 mb-2">
          {{ t('PIPELINE.ACCESS_DENIED.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('PIPELINE.ACCESS_DENIED.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <template v-else>
      <div class="flex flex-col h-full w-full bg-n-background">
        <PipelineHeader
          :current-view="currentView"
          :search-results="searchResults"
          :active-filters="activeFilters"
          :pipeline-cards="pipelineCards"
          :has-partial-dataset="hasPartialDataset"
          @filter="handleFilter"
          @search="handleSearch"
          @switch-view="view => emit('switch-view', view)"
        />

        <!-- List Toolbar (B5 - List View Enhancement) -->
        <div
          class="flex flex-wrap items-center justify-between gap-2 px-3 py-2 sm:px-4 border-b border-n-weak bg-n-solid-2"
        >
          <div class="flex min-w-0 flex-wrap items-center gap-2 sm:gap-4">
            <span class="shrink-0 text-sm text-n-slate-11" aria-live="polite">
              {{ filteredDeals.length }}
              {{
                filteredDeals.length === 1
                  ? t('PIPELINE.CARD_SINGULAR')
                  : t('PIPELINE.CARDS')
              }}
            </span>
            <div class="flex min-w-0 items-center gap-1.5">
              <label for="pipeline-list-sort" class="sr-only">
                {{ t('PIPELINE.SORT_MODAL.SORT_BY') }}
              </label>
              <Select
                v-model="sortConfig.field"
                input-id="pipeline-list-sort"
                name="pipelineListSort"
                :aria-label="t('PIPELINE.SORT_MODAL.SORT_BY')"
                :options="sortOptions"
                class="min-w-0 max-w-48 [&>div]:w-full [&_select]:w-full"
              />
              <Button
                variant="ghost"
                color="slate"
                size="md"
                class="min-h-11 min-w-11"
                :aria-label="
                  sortConfig.direction === 'asc'
                    ? t('PIPELINE.SORT_MODAL.ASCENDING')
                    : t('PIPELINE.SORT_MODAL.DESCENDING')
                "
                :title="
                  sortConfig.direction === 'asc'
                    ? t('PIPELINE.SORT_MODAL.ASCENDING')
                    : t('PIPELINE.SORT_MODAL.DESCENDING')
                "
                @click="toggleSortDirection"
              >
                <template #icon>
                  <fluent-icon
                    :icon="
                      sortConfig.direction === 'asc'
                        ? 'chevron-up'
                        : 'chevron-down'
                    "
                    size="16"
                  />
                </template>
              </Button>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <Button
              variant="ghost"
              color="slate"
              size="md"
              class="min-h-11"
              :disabled="!filteredDeals.length"
              :title="t('PIPELINE.LIST.EXPORT_CSV')"
              :aria-label="t('PIPELINE.LIST.EXPORT_CSV')"
              @click="exportToCSV"
            >
              <template #icon>
                <fluent-icon icon="arrow-download" size="16" />
              </template>
              <span class="hidden sm:inline">{{
                t('PIPELINE.LIST.EXPORT_CSV')
              }}</span>
            </Button>
          </div>
        </div>

        <div class="min-h-0 w-full p-4 flex-1 overflow-auto">
          <!-- Loading State -->
          <div
            v-if="isLoading"
            class="flex items-center justify-center py-12"
            role="status"
            :aria-label="t('PIPELINE.LOADING_ITEMS')"
          >
            <span
              class="h-6 w-6 animate-spin rounded-full border-2 border-n-weak border-t-n-brand motion-reduce:animate-none"
              aria-hidden="true"
            />
            <span class="sr-only">{{ t('PIPELINE.LOADING_ITEMS') }}</span>
          </div>

          <!-- Error State -->
          <div
            v-else-if="pipelineLoadError"
            role="alert"
            class="flex flex-col items-center justify-center gap-3 py-12 text-center text-n-ruby-11"
          >
            <p>{{ pipelineLoadError }}</p>
            <button
              type="button"
              class="min-h-11 rounded-lg border border-n-weak px-4 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
              @click="retryListLoad"
            >
              {{ t('PIPELINE.MESSAGES.RETRY') }}
            </button>
          </div>

          <!-- Empty State -->
          <div
            v-else-if="!filteredDeals.length"
            role="status"
            class="flex flex-col items-center justify-center py-12 text-center"
          >
            <fluent-icon
              icon="checkmark-circle"
              size="48"
              class="mb-4 text-n-slate-10"
            />
            <p class="text-lg text-n-slate-11">
              {{
                hasClientSideFiltering
                  ? t('PIPELINE.NO_FILTERED_ITEMS')
                  : t('PIPELINE.NO_ITEMS')
              }}
            </p>
          </div>

          <!-- Simplified List View -->
          <div v-else class="space-y-2 w-full max-w-full">
            <div
              v-for="stage in groupedDeals"
              :key="stage.id"
              class="w-full bg-n-solid-2 rounded-xl outline outline-1 outline-n-weak animate-fade-in"
            >
              <!-- Stage Header -->
              <button
                type="button"
                class="flex min-h-11 w-full items-center justify-between gap-3 rounded-t-xl bg-n-solid-3 p-3 text-left focus-visible:outline-2 focus-visible:outline-n-brand"
                :aria-expanded="isStageExpanded(stage.id)"
                :aria-controls="`pipeline-list-stage-${stage.id}`"
                @click="toggleStage(stage.id)"
              >
                <div class="flex items-center gap-2">
                  <span
                    class="w-3 h-3 rounded-full"
                    :class="getStageColorClasses(stage.color).dot"
                  />
                  <h3 class="font-medium text-n-slate-12">
                    {{ stage.name }}
                  </h3>
                  <!-- Ciclo 7 — total real do stage (meta.stage_counts);
                       parcial vira "carregados de total". -->
                  <span
                    class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
                  >
                    {{ stageCountLabel(stage) }}
                  </span>
                </div>
                <div class="flex items-center gap-2">
                  <div class="text-right text-sm font-medium text-n-slate-12">
                    {{ formatStageTotal(stage) }}
                  </div>
                  <fluent-icon
                    :icon="
                      isStageExpanded(stage.id) ? 'chevron-up' : 'chevron-down'
                    "
                    size="16"
                    class="text-n-slate-11"
                  />
                </div>
              </button>

              <!-- Column Headers -->
              <div
                v-show="isStageExpanded(stage.id)"
                class="hidden md:grid md:[grid-template-columns:minmax(0,2fr)_minmax(0,1fr)_auto_auto_auto_auto] items-center gap-3 px-4 py-1.5 ml-1 border-b border-n-weak bg-n-alpha-1 text-[11px] font-medium text-n-slate-11 uppercase tracking-wider"
              >
                <span>{{ t('PIPELINE.FORM.TITLE.LABEL') }}</span>
                <span>{{ t('PIPELINE.AGENT.LABEL') }}</span>
                <span class="w-16 text-center">{{
                  t('PIPELINE.PANELS.LEAD_SCORE')
                }}</span>
                <span class="w-20 text-center">{{
                  t('PIPELINE.FORM.PRIORITY.LABEL')
                }}</span>
                <span class="w-24 text-right">{{
                  t('PIPELINE.VALUE.LABEL')
                }}</span>
                <span class="w-16" />
              </div>

              <!-- Deals List — UX C1 (2026-04-22): renderiza apenas
                   getVisibleDeals(stage) (default 30 por stage).
                   Botão "Carregar mais" abaixo incrementa em +30. -->
              <div
                v-show="isStageExpanded(stage.id)"
                :id="`pipeline-list-stage-${stage.id}`"
                class="divide-y divide-n-weak"
              >
                <div
                  v-for="deal in getVisibleDeals(stage)"
                  :key="deal.id"
                  class="group flex items-stretch p-0 bg-n-solid-2 hover:bg-n-alpha-1 transition-colors duration-150 border-b border-n-weak last:border-0"
                >
                  <!-- Priority indicator bar -->
                  <div
                    class="w-1 flex-shrink-0 transition-colors"
                    :class="getPriorityBarClass(deal.priority)"
                  />

                  <div
                    class="flex flex-1 flex-col gap-3 px-3 py-3 md:grid md:items-center md:gap-3 md:px-4 md:[grid-template-columns:minmax(0,2fr)_minmax(0,1fr)_auto_auto_auto_auto]"
                  >
                    <!-- Col 1: Contact avatar + Title/Description -->
                    <button
                      type="button"
                      class="flex min-h-11 min-w-0 items-center gap-3 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                      :aria-label="deal.title || t('PIPELINE.NO_TITLE')"
                      @click="handleItemClick(deal)"
                    >
                      <span
                        v-if="deal.contactThumbnail"
                        class="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden"
                      >
                        <img
                          :src="deal.contactThumbnail"
                          :alt="deal.contactName"
                          class="w-full h-full object-cover"
                        />
                      </span>
                      <span
                        v-else
                        class="flex-shrink-0 w-8 h-8 rounded-full bg-n-alpha-2 flex items-center justify-center text-xs font-medium text-n-slate-11"
                      >
                        {{ (deal.title || '?').charAt(0).toUpperCase() }}
                      </span>
                      <div class="min-w-0">
                        <div
                          class="font-medium text-sm text-n-slate-12 truncate"
                        >
                          {{ deal.title }}
                        </div>
                        <div
                          v-if="deal.description"
                          class="text-xs text-n-slate-11 truncate"
                        >
                          {{ deal.description.substring(0, 60) }}
                        </div>
                      </div>
                    </button>

                    <!-- Col 2: Agent -->
                    <div
                      class="flex items-center gap-2 min-w-0"
                      :title="deal.assignee?.name"
                    >
                      <template v-if="deal.assignee">
                        <img
                          v-if="deal.assignee.avatar"
                          :src="deal.assignee.avatar"
                          :alt="deal.assignee.name"
                          class="flex-shrink-0 w-6 h-6 rounded-full object-cover"
                        />
                        <span
                          v-else
                          class="flex-shrink-0 w-6 h-6 rounded-full bg-n-brand flex items-center justify-center text-[10px] font-medium text-white"
                        >
                          {{ deal.assignee.name.charAt(0) }}
                        </span>
                        <span class="text-xs text-n-slate-11 truncate">
                          {{ deal.assignee.name }}
                        </span>
                      </template>
                      <span v-else class="text-xs text-n-slate-10 italic">
                        {{ t('PIPELINE.NO_AGENT') }}
                      </span>
                    </div>

                    <!-- Col 3: Lead Score -->
                    <div
                      class="flex w-full items-center justify-start md:w-16 md:justify-center"
                    >
                      <span
                        v-if="deal.leadScore != null"
                        class="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full"
                        :class="getLeadScoreBadgeClass(deal.leadScore)"
                      >
                        {{ deal.leadScore }}
                      </span>
                      <span v-else class="text-xs text-n-slate-9">{{
                        t('PIPELINE.LIST.EMPTY_CELL')
                      }}</span>
                    </div>

                    <!-- Col 4: Priority Badge -->
                    <div
                      class="flex w-full items-center justify-start md:w-20 md:justify-center"
                    >
                      <span
                        v-if="deal.priority && deal.priority !== 'none'"
                        class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border"
                        :class="getPriorityBadgeClass(deal.priority)"
                      >
                        <fluent-icon icon="alert" size="10" />
                        {{
                          t(
                            `PIPELINE.PRIORITY_LABELS.${deal.priority.toUpperCase()}`
                          )
                        }}
                      </span>
                      <span v-else class="text-xs text-n-slate-9">{{
                        t('PIPELINE.LIST.EMPTY_CELL')
                      }}</span>
                    </div>

                    <!-- Col 5: Value + Date stacked -->
                    <div
                      class="flex w-full items-center justify-between gap-3 text-left md:block md:w-24 md:text-right"
                    >
                      <div
                        class="text-sm font-semibold text-n-slate-12 tabular-nums"
                      >
                        {{ formatDealValue(deal) }}
                      </div>
                      <div class="text-[11px] text-n-slate-10 tabular-nums">
                        {{ formatDealDate(deal.createdAt) }}
                      </div>
                    </div>

                    <!-- Col 6: Actions -->
                    <div
                      class="flex w-full items-center justify-end gap-1 opacity-100 transition-opacity duration-150 md:w-24 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100"
                    >
                      <button
                        type="button"
                        class="flex min-h-11 min-w-11 items-center justify-center rounded-lg text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 focus-visible:outline-2 focus-visible:outline-n-brand transition-colors"
                        :aria-label="t('PIPELINE.EDIT')"
                        :title="t('PIPELINE.EDIT')"
                        @click.stop="handleEdit(deal)"
                      >
                        <fluent-icon icon="edit" size="14" />
                      </button>
                      <button
                        v-if="canDeleteCard"
                        type="button"
                        class="flex min-h-11 min-w-11 items-center justify-center rounded-lg text-n-slate-11 hover:text-n-ruby-11 hover:bg-n-ruby-3 focus-visible:outline-2 focus-visible:outline-n-brand transition-colors"
                        :aria-label="t('PIPELINE.DELETE')"
                        :title="t('PIPELINE.DELETE')"
                        @click.stop="handleDelete(deal)"
                      >
                        <fluent-icon icon="delete" size="14" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- UX C1: "Carregar mais" botão quando stage tem mais
                   deals que o limit atual de render. -->
              <div
                v-if="isStageExpanded(stage.id) && hasMoreDeals(stage)"
                class="px-4 py-3 border-t border-n-weak bg-n-alpha-1 flex items-center justify-center"
              >
                <button
                  type="button"
                  class="min-h-11 text-sm font-medium text-n-brand hover:text-n-brand-emphasis hover:bg-n-alpha-2 px-4 py-2 rounded-lg focus-visible:outline-2 focus-visible:outline-n-brand transition-colors flex items-center gap-2"
                  @click="loadMoreDeals(stage.id)"
                >
                  <fluent-icon icon="chevron-down" size="14" />
                  {{
                    t('PIPELINE.LIST.LOAD_MORE', {
                      count: remainingDealsCount(stage),
                    })
                  }}
                </button>
              </div>
            </div>

            <!-- Ciclo 7 — rodapé de paginação global por cursor: remove o
                 teto silencioso de 500 cards da lista. Visível enquanto o
                 backend sinaliza has_more + next_cursor no fetch global. -->
            <div
              v-if="hasMoreFromServer"
              data-testid="list-server-pagination"
              class="flex flex-col items-center gap-2 py-4"
            >
              <span
                v-if="serverTotalCount > deals.length"
                class="text-xs text-n-slate-10"
              >
                {{
                  t('PIPELINE.LIST.SHOWING_OF', {
                    loaded: deals.length,
                    total: serverTotalCount,
                  })
                }}
              </span>
              <Button
                variant="faded"
                color="slate"
                size="sm"
                class="min-h-11"
                data-testid="list-load-more"
                :is-loading="isAppendingFromServer"
                :disabled="isAppendingFromServer"
                @click="handleLoadMoreFromServer"
              >
                <template #icon>
                  <fluent-icon icon="chevron-down" size="16" />
                </template>
                {{ t('PIPELINE.LOAD_MORE_SIMPLE') }}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <PipelineFilter
        :show="showFilterModal"
        :filtered-results="searchResults"
        :current-pipeline="selectedPipeline"
        :initial-filters="activeFilters"
        scope="global"
        @apply="applyFilters"
        @close="showFilterModal = false"
      />

      <Modal v-model:show="showDeleteModal" @close="closeDeleteModal">
        <div class="p-6">
          <h3 class="text-lg font-medium mb-4">
            {{ t('PIPELINE.DELETE_CONFIRMATION.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mb-6">
            {{
              t('PIPELINE.DELETE_CONFIRMATION.DESCRIPTION', {
                name: itemToDelete?.title,
              })
            }}
          </p>
          <div class="flex justify-end gap-2">
            <Button
              variant="ghost"
              color="slate"
              size="md"
              :disabled="isDeleting"
              @click="closeDeleteModal"
            >
              {{ t('PIPELINE.CANCEL') }}
            </Button>
            <Button
              variant="solid"
              color="ruby"
              size="md"
              :is-loading="isDeleting"
              :disabled="isDeleting || !itemToDelete"
              @click="confirmDelete"
            >
              {{ t('PIPELINE.DELETE') }}
            </Button>
          </div>
        </div>
      </Modal>

      <!-- Bug 3 (2026-04-22): Modal de edição do card na view Lista.
         Antes handleEdit setava showEditModal=true mas não havia modal
         renderizada, fazendo o lápis não responder. Agora usa o mesmo
         padrão de AgendaTab/PipelineCardDetailView (PipelineCardForm
         com is-editing + :initial-data). -->
      <Modal
        v-model:show="showEditModal"
        :before-close="requestEditModalClose"
        @close="
          () => {
            showEditModal = false;
            itemToEdit = null;
          }
        "
      >
        <div class="w-[560px] max-w-[calc(100vw-2rem)]">
          <PipelineCardForm
            v-if="itemToEdit && selectedPipeline"
            ref="editCardFormRef"
            :pipeline-id="selectedPipeline.id"
            :stage="itemToEdit.pipeline_stage"
            :initial-data="itemToEdit"
            is-editing
            @saved="handleItemUpdated"
            @close="
              () => {
                showEditModal = false;
                itemToEdit = null;
              }
            "
          />
        </div>
      </Modal>
    </template>
  </div>
</template>
